import React from 'react';
import { Modal } from '../ui/Modal';
import { InviteToken } from '../../utils/inviteHelpers';

interface InviteConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    inviteData: InviteToken | null;
    isProcessing: boolean;
}

export const InviteConfirmationModal: React.FC<InviteConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    inviteData,
    isProcessing
}) => {
    if (!inviteData) return null;

    const isTournament = inviteData.type === 'tournament';
    const targetName = isTournament ? inviteData.tournamentName : inviteData.groupName;
    const inviterName = inviteData.createdByName || 'Un utilisateur';

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Invitation reçue">
            <div className="flex flex-col items-center space-y-6 text-center">
                {/* Icon with gradient background */}
                <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-purple-500/20 rounded-2xl flex items-center justify-center border border-primary/30 shadow-lg shadow-primary/10">
                        <span className="material-symbols-outlined text-4xl text-primary">
                            {isTournament ? 'emoji_events' : 'group_add'}
                        </span>
                    </div>
                    {/* Floating envelope badge */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-success rounded-full flex items-center justify-center shadow-lg animate-bounce">
                        <span className="material-symbols-outlined text-white text-sm">mail</span>
                    </div>
                </div>

                {/* Invite text */}
                <div className="space-y-3">
                    <p className="text-text-secondary">
                        <span className="font-bold text-text-primary">{inviterName}</span> vous invite à rejoindre {isTournament ? 'le tournoi' : 'le groupe'} :
                    </p>
                    <h3 className="text-2xl font-black bg-gradient-to-r from-primary to-purple-400 bg-clip-text text-transparent">
                        {targetName}
                    </h3>
                </div>

                {/* Info card */}
                <div className="p-4 bg-surface-2 rounded-xl border border-glass-border w-full">
                    <div className="flex items-start gap-3">
                        <span className="material-symbols-outlined text-primary text-xl mt-0.5">info</span>
                        <p className="text-sm text-text-secondary text-left">
                            En acceptant, vous deviendrez membre {isTournament ? 'du tournoi' : 'du groupe'} et pourrez participer aux activités.
                        </p>
                    </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 w-full pt-2">
                    {/* Decline button */}
                    <button
                        onClick={onClose}
                        disabled={isProcessing}
                        className="flex-1 px-5 py-3 rounded-xl bg-surface-2 border border-glass-border text-text-secondary font-semibold hover:bg-surface-3 hover:text-text-primary hover:border-white/20 disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2 group"
                    >
                        <span className="material-symbols-outlined text-lg group-hover:text-error transition-colors">close</span>
                        Refuser
                    </button>

                    {/* Accept button */}
                    <button
                        onClick={onConfirm}
                        disabled={isProcessing}
                        className="flex-1 px-5 py-3 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-bold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 transition-all duration-200 flex items-center justify-center gap-2"
                    >
                        {isProcessing ? (
                            <>
                                <span className="material-symbols-outlined text-lg animate-spin">autorenew</span>
                                Traitement...
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined text-lg">check_circle</span>
                                Rejoindre
                            </>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
